import { dmnStorage } from "../dmnStorage";
import { ViewBooking } from "collins-js-sdk";
import {
  editBooking,
  mapBookingToV4,
  mapBookingV4ToState
} from "../../APIs/Booking/UpdateBooking";
import { convertDateToUTC } from "../helpers";
import { BookingV4 } from "../../types/bookings";

type storageKeyType = "get" | "update";
export type apiCacheStorageType = {
  storageKey: storageKeyType;
  data?: any;
  entered: Date;
  resource: RequestInfo | URL;
  config?: RequestInit;
};

export const addApiCacheStorage = async (props: apiCacheStorageType) => {
  if (props.config?.headers) {
    for (const [key] of Object.entries(props.config.headers)) {
      if (key === "Authorization") {
        // don't store the token in local storage
        delete (props.config.headers as any)[key];
      }
    }
  }

  let existingData = await getApiCacheStorage(props.storageKey);

  let updated = false;
  existingData = existingData.map((data) => {
    if (data.resource === props.resource) {
      updated = true;
      return props;
    }
    return data;
  });

  if (!updated) {
    existingData.push(props);
  }

  /*const size = new TextEncoder().encode(JSON.stringify(existingData)).length;
  const kiloBytes = size / 1024;
  const megaBytes = kiloBytes / 1024;*/

  return await dmnStorage.add(props.storageKey, existingData);
};

export const getApiCacheStorage = async (
  storageKey: storageKeyType
): Promise<apiCacheStorageType[]> => {
  const storageData: apiCacheStorageType[] = await dmnStorage.get(storageKey);

  return purgeOldData(storageData ?? []);
};

export const getApiCacheRequestStorage = async (
  props: apiCacheStorageType
): Promise<apiCacheStorageType | undefined> => {
  const storageData = await getApiCacheStorage(props.storageKey);

  const response = storageData?.find(
    (data) =>
      data.resource === props.resource &&
      data.config?.method === props.config?.method
  );

  return response;
};

export const deleteApiCacheStorage = async (storageKey: storageKeyType) => {
  return await dmnStorage.delete(storageKey);
};

const purgeOldData = (data?: apiCacheStorageType[]): apiCacheStorageType[] => {
  const maxAgeHr = 24;
  const today = new Date();

  return (
    data?.filter?.((request) => {
      const entered = new Date(request.entered);
      const ms = Math.abs(entered.getTime() - today.getTime());
      const hours = ms / (60 * 60 * 1000);
      return hours < maxAgeHr;
    }) ?? []
  );
};

export type updateBookingCacheStorageProps = {
  booking: ViewBooking & editBooking;
  venueId?: string;
};

export const updateBookingCacheStorage = async (
  props: updateBookingCacheStorageProps
) => {
  const storageData = await getApiCacheStorage("get");

  const bookingDate = convertDateToUTC(
    new Date(props.booking.date ?? "")
  ).toLocaleDateString("en-CA");

  let updated = false;

  const updatedCache = storageData?.map((store) => {
    if (updated) {
      return store;
    }

    if (
      !props.booking.id &&
      store.resource.toString().indexOf(`bookings?date=${bookingDate}`) > -1 &&
      store.resource.toString().indexOf(`venue_id=${props.venueId}`) > -1
    ) {
      updated = true;
      return {
        ...store,
        data: {
          ...store.data,
          bookings: [
            ...(store.data?.bookings || []),
            mapBookingToV4(props.booking)
          ]
        }
      };
    }

    return {
      ...store,
      data: {
        ...store.data,
        bookings: store.data?.bookings?.map((storeBooking: ViewBooking) => {
          if (storeBooking.id === props.booking.id) {
            updated = true;
            return mapBookingToV4(props.booking);
          }

          return storeBooking;
        })
      }
    };
  });

  if (!updatedCache || !updated) {
    return;
  }

  return await dmnStorage.add("get", updatedCache);
};

export const getBookingCacheStorage = async (
  date: Date,
  venueId?: string
): Promise<ViewBooking[]> => {
  const bookingDate = convertDateToUTC(date).toLocaleDateString("en-CA");
  const storageData = await getApiCacheStorage("get");

  const bookingCache = storageData?.find(
    (store) =>
      store.resource.toString().indexOf(`bookings?date=${bookingDate}`) > -1 &&
      store.resource.toString().indexOf(`venue_id=${venueId}`) > -1
  );

  return (
    bookingCache?.data?.bookings.map((booking: BookingV4) =>
      mapBookingV4ToState(booking)
    ) || []
  );
};
